import { useContext } from "react";
import { motion } from "framer-motion";
import { LanguageContext } from "@/i18n";

const EASE = [0.16, 1, 0.3, 1];

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: EASE },
};

export default function Vision() {
  const { t } = useContext(LanguageContext);

  return (
    <section id="vision" data-testid="vision-section" className="relative py-28 md:py-40">
      <div className="max-w-screen-xl mx-auto px-6 md:px-12">
        <motion.p {...fadeUp} className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#00F0FF] mb-6">
          {t.vision.label}
        </motion.p>

        <motion.h2
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="font-display font-black tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl max-w-4xl leading-[1.02]"
        >
          {t.vision.heading}
        </motion.h2>

        <div className="mt-16 grid md:grid-cols-2 gap-10 md:gap-16">
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="text-base md:text-lg font-light text-zinc-400 leading-relaxed">
            {t.vision.p1}
          </motion.p>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.25 }} className="text-base md:text-lg font-light text-zinc-400 leading-relaxed">
            {t.vision.p2}
          </motion.p>
        </div>

        <div className="mt-24 border-t border-white/10">
          {t.vision.chapters.map((c, i) => (
            <motion.div
              key={c.num}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: EASE }}
              className="group grid md:grid-cols-[120px_1fr_1.4fr] gap-4 md:gap-10 py-10 border-b border-white/10 hover:bg-white/[0.015] transition-colors duration-500 px-2 md:px-4"
            >
              <span className="font-mono text-sm text-[#00F0FF]/70 pt-1">{c.num}</span>
              <h3 className="font-display font-bold text-xl md:text-2xl text-white group-hover:text-[#00F0FF] transition-colors duration-500">
                {c.title}
              </h3>
              <p className="text-sm md:text-base font-light text-zinc-500 leading-relaxed">{c.text}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden">
          {t.vision.stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
              className="bg-[#0D0D0D] p-8 md:p-10 flex flex-col gap-2 hover:bg-[#111111] transition-colors duration-500"
            >
              <span className="font-display font-black text-3xl md:text-4xl text-white">{s.value}</span>
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">{s.label}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
